import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, VideoFile } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useDropzone, Accept } from 'react-dropzone';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { VideoAssetBody, VideoAssetResponse, VideoAssetRole } from '../../../API';
import { useData } from '../../../data-layer';
import { useLocales, useNotifications, useTheme } from '../../../hooks';
import Button from '../../shared/Button';
import Modal from '../../shared/Modal';
import { bigSize } from '../../shared/Modal/Modal';
import { isFormValid } from '../../../utils/formHelpers';
import { AssetGuidelines } from '../../../utils/assetGuidelines';
import { AssetTypesEnum } from '../../../utils/assetTypes';
import TextValidator from '../../shared/TextValidator';
import { assetUploaderTestIds } from '../../shared/TestsIds';

const useStyles = makeStyles()((theme) => ({
  form: {
    height: '100%'
  },
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2)
  },
  filePicker: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px dashed rgba(255 255 255 / 50%)',
    height: '100%',
    minHeight: 200,
    cursor: 'pointer',
    borderRadius: 10
  },
  fileEditContainer: {
    height: '100%',
    display: 'flex'
  },
  imageFocusControls: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    padding: theme.spacing(2, 4, 2, 2)
  },
  footerButton: {
    minWidth: 140
  },
  modal: bigSize,
  guidelines: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.secondary
  },
  guidelinesHeader: {
    marginBottom: theme.spacing(2)
  },
  guidelinesLabel: {
    fontWeight: 'bold',
    marginRight: theme.spacing(2)
  },
  assetTypeLabel: {
    fontWeight: 'bold',
    fontFamily: 'monospace'
  },
  previewVideoContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2)
  },
  fileInfoContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2)
  },
  fileIcon: {
    marginBottom: theme.spacing(2)
  },
  fileDetails: {
    textAlign: 'center'
  }
}));

type FilePreview = File & {
  preview: string;
};

type VideoAssetUploaderProps = {
  assetId?: string;
  assetType: AssetTypesEnum;
  open: boolean;
  onAssetUpload?: (asset: VideoAssetResponse, isEditing: boolean) => void;
  onClose?: () => void;
  acceptedFileTypes?: Accept;
  editingAsset?: VideoAssetResponse;
};

export function VideoAssetUploader({
  assetId = '',
  assetType,
  open,
  onAssetUpload,
  onClose,
  acceptedFileTypes = { 'video/*': ['.mp4', '.mov', '.webm'] },
  editingAsset
}: VideoAssetUploaderProps): JSX.Element {
  const { classes } = useStyles();
  const { t } = useLocales();
  const { formControlColor } = useTheme();
  const formRef = useRef<ValidatorForm>(null);
  const [file, setFile] = useState<FilePreview | undefined>();
  const [isOpen, setIsOpen] = useState(open);
  const { notifyError } = useNotifications();
  const [isUploading, setIsUploading] = useState(false);
  const { handleSubmit, control, reset } = useForm<VideoAssetResponse>();

  const {
    videoAssets: {
      hook: { create, update }
    }
  } = useData();

  // when file is selected
  const onDrop = useCallback((files: File[]) => {
    if (files && files.length) {
      const file = files[0] as FilePreview;
      setFile(file);
      const asset: Partial<VideoAssetResponse> = {
        assetId,
        assetType,
        assetName: stripExtension(file.name),
        videoAssetRole: VideoAssetRole.BACKGROUND_FILL
      };
      reset(asset);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: acceptedFileTypes
  });

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  useEffect(() => {
    if (editingAsset) {
      setIsOpen(true);

      reset(editingAsset);
    }
  }, [editingAsset]);

  const stripExtension = (fileName: string) => {
    const match = fileName.match(/^(.*)\.\w+$/);
    return match?.[1] ?? fileName;
  };

  const handleUploadClick = async () => {
    if (!formRef.current) return;
    if (!editingAsset && !file) {
      return notifyError(t('errors.assets.validate_no_file'));
    }
    const valid = await isFormValid(formRef);
    if (!valid) {
      notifyError(t('general.form_error'));
    }

    (formRef.current as ValidatorForm & { submit: () => void }).submit();
  };

  const doSubmit = async (asset: VideoAssetResponse) => {
    setIsUploading(true);
    let savedAsset;
    if (editingAsset) {
      savedAsset = await update(asset.id, asset);
    } else if (file) {
      savedAsset = await create(
        {
          assetId: asset.assetId,
          assetName: asset.assetName,
          assetType,
          videoAssetRole: asset.videoAssetRole
        } as VideoAssetBody,
        false,
        file
      );
    }
    setIsUploading(false);
    if (savedAsset) {
      onAssetUpload?.(savedAsset, !!editingAsset);
    }
    handleClose();
  };

  const onSubmit: SubmitHandler<VideoAssetResponse> = (asset) => {
    doSubmit(asset);
  };

  const reinitialize = () => {
    setFile(undefined);
  };

  const handleClose = () => {
    setIsOpen(false);
    reinitialize();
    onClose?.();
  };

  const guidelines = AssetGuidelines[assetType];

  return (
    <Modal
      open={isOpen}
      bodyClassName={classes.modal}
      onClose={handleClose}
      headerLeft={
        <Typography variant="h6">{t(editingAsset ? 'assets.edit_video_asset' : 'assets.new_video_asset')}</Typography>
      }
      footerLeft={
        <>
          <Button
            className={classes.footerButton}
            loading={isUploading}
            endIcon={<Upload />}
            variant="contained"
            onClick={handleUploadClick}
            data-testid={assetUploaderTestIds.formSubmit}
          >
            {t(editingAsset ? 'general.save_changes' : 'general.upload')}
          </Button>
        </>
      }
      footerRight={
        <Button className={classes.footerButton} color="grey" onClick={handleClose}>
          {t('general.cancel')}
        </Button>
      }
    >
      <ValidatorForm
        className={classes.form}
        ref={formRef}
        onSubmit={handleSubmit(onSubmit)}
        data-testid={assetUploaderTestIds.validatorForm}
      >
        <div className={classes.root}>
          {!file && !editingAsset && (
            <div className={classes.filePicker} {...getRootProps()}>
              <Stack direction="column" gap={6} alignItems="center">
                {isDragActive ? t('assets.drop_file') : t('assets.drag_and_drop')}
                {guidelines && (
                  <div className={classes.guidelines}>
                    <div className={classes.guidelinesHeader}>
                      {t('assets.guidelines.guidelines_for_type')}{' '}
                      <span className={classes.assetTypeLabel}>{assetType}</span>
                    </div>
                    {guidelines.preferredFileTypes && (
                      <div>
                        <span className={classes.guidelinesLabel}>{t('assets.guidelines.preferred_file_types')}:</span>
                        {guidelines.preferredFileTypes.join(', ')}
                      </div>
                    )}
                    {guidelines.preferredAspectRatio && (
                      <div>
                        <span className={classes.guidelinesLabel}>
                          {t('assets.guidelines.preferred_aspect_ratio')}:
                        </span>
                        {guidelines.preferredAspectRatio}
                      </div>
                    )}
                    {guidelines.minHeight && (
                      <div>
                        <span className={classes.guidelinesLabel}>{t('assets.guidelines.recommended_width')}:</span>
                        {guidelines.minWidth}px
                      </div>
                    )}
                    {guidelines.minHeight && (
                      <div>
                        <span className={classes.guidelinesLabel}>{t('assets.guidelines.recommended_height')}:</span>
                        {guidelines.minHeight}px
                      </div>
                    )}
                  </div>
                )}
              </Stack>
              <input data-testid={assetUploaderTestIds.fileInput} {...getInputProps()} />
            </div>
          )}
          {(file || editingAsset) && (
            <div className={classes.fileEditContainer}>
              <div className={classes.imageFocusControls}>
                <Controller
                  name="assetName"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <TextValidator
                      name="assetName"
                      required
                      fullWidth
                      color={formControlColor}
                      value={value}
                      onChange={onChange}
                      label={t('assets.name')}
                      validators={['required']}
                      errorMessages={[t('assets.asset_name_required')]}
                      data-testid={assetUploaderTestIds.assetName}
                    />
                  )}
                />
              </div>
              <div className={classes.previewVideoContainer}>
                {editingAsset?.url && (
                  <video
                    controls
                    width={editingAsset?.aspectRatio === '16:9' ? '640' : '360'}
                    height={editingAsset?.aspectRatio === '16:9' ? '360' : '640'}
                    loop
                    autoPlay
                  >
                    <source src={editingAsset?.url} type={editingAsset?.mediaType} />
                  </video>
                )}
                {!editingAsset?.url && (
                  <div className={classes.fileInfoContainer}>
                    <div className={classes.fileIcon}>
                      <VideoFile sx={{ fontSize: 48 }} />
                    </div>
                    <div className={classes.fileDetails}>
                      <Typography variant="subtitle1">{file?.name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {file?.size ? `${sizeToMB(file.size)} MB` : ''}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {file?.type}
                      </Typography>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </ValidatorForm>
    </Modal>
  );
}

const sizeToMB = (size: number) => {
  return Math.round(size / 1024 / 1024);
};
